import { ApiRequestOptions, MoDataStore } from 'ui-common';
import { AgentStudioDataSourceStoreMockPlugin } from './mocks/AgentStudioDataSourceStoreMockPlugin';
import { get, set } from 'lodash';
import { AgentStudioDataSource, AgentStudioDataSourceLink, IndexTarget } from '../../components/app-studio/agent-studio/interfaces';

export class AgentStudioDataSourceStore extends MoDataStore {
  static defaultConfig: any = {
    mockPlugin: AgentStudioDataSourceStoreMockPlugin,
    api: {
      service: 'datasources',
      // returns a list of only the data sources linked to the index target
      // hooks into the load mechanism of the store
      list: 'getIndexTargetDataSources',
      // uses the solutions endpoint
      get: 'getAgentStudioDataSource',
      add: 'createAgentStudioDataSource',
      update: 'updateAgentStudioDataSource',
      remove: 'deleteAgentStudioDataSource',
    }
  };

  private isInitialized: boolean = false;

  constructor(config?: any) {
    super(config);
    this.alwaysRefreshData = true;
  }

  getDataSourceCounts() {
    return get(this, 'data.length', 0);
  }

  protected override getLoadingParams(): ApiRequestOptions {
    const pathParams = {
      pathParams: {
        indexTargetId: get(this.config, 'indexTargetId')
      }
    };
    return {
      ...super.getLoadingParams(),
      ...pathParams
    };
  }

  // Ensures the store is properly initialized with an index target ID
  // before any operations can be performed
  async initializeStore(indexTargetId: IndexTarget['id']): Promise<boolean> {
    if (indexTargetId) {
      set(this.config, 'indexTargetId', indexTargetId);
      this.isInitialized = true;
      return true;
    }
    throw new Error('AgentStudioDataSourceStore: Cannot initialize with empty indexTargetId');
  }

  // Load calls should always be preceded by a call to initializeStore
  // to ensure the index target ID is set
  override async load(): Promise<any> {
    if (!this.isInitialized) {
      throw new Error('Cannot load data sources: Store not initialized. Please call initializeStore first.');
    }
    return super.load();
  }

  // INDEX TARGET DATA SOURCE LINKS
  async getIndexTargetDataSourceLink(indexTargetId: IndexTarget['id'], dataSourceId: AgentStudioDataSource['id']) {
    const pathParams = {
      pathParams: {
        indexTargetId,
        dataSourceId
      }
    };
    return await this.callApiMethod({
      service: 'datasources',
      name: 'getIndexTargetDataSourceLink'
    }, {}, pathParams);
  }

  async createIndexTargetDataSourceLink(dataSourceLink: AgentStudioDataSourceLink, dataSourceId: AgentStudioDataSource['id']) {
    const pathParams = {
      pathParams: {
        indexTargetId: dataSourceLink.indexTargetId,
        dataSourceId
      }
    };
    return await this.callApiMethod({
      service: 'datasources',
      name: 'createIndexTargetDataSourceLink',
      data: dataSourceLink
    }, {}, pathParams);
  }

  async updateIndexTargetDataSourceLink(dataSourceLink: AgentStudioDataSourceLink, dataSourceId: AgentStudioDataSource['id'],) {
    const pathParams = {
      pathParams: {
        indexTargetId: dataSourceLink.indexTargetId,
        dataSourceId
      }
    };
    return await this.callApiMethod({
      service: 'datasources',
      name: 'updateIndexTargetDataSourceLink',
      data: dataSourceLink
    }, {}, pathParams);
  }

  async removeIndexTargetDataSourceLink(indexTargetId: IndexTarget['id'], dataSourceId: AgentStudioDataSource['id']) {
    const pathParams = {
      pathParams: {
        indexTargetId,
        dataSourceId
      }
    };
    return await this.callApiMethod({
      service: 'datasources',
      name: 'deleteIndexTargetDataSourceLink'
    }, {}, pathParams);
  }

  async getDataSourceStats(customerId: string, dataSourceId: string) {
    const pathParams = {
      pathParams: {
        customerId,
        dataSourceId
      }
    };
    return await this.callApiMethod({
      service: 'stats',
      name: 'getDataSourceStats'
    }, {}, pathParams);
  }
}

